import { useEffect, useRef } from 'react';

import { getCookie } from '@api/HelperAPI';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { tracking } from '@iwoca/frontend-tracking-library';
import { getCurrentLocation } from '@iwRouter/HistoryProvider';
import { FormattedMessage } from 'react-intl';

import { SpillBanner } from '../spill/SpillBanner';
import { useShowSpillBanner } from '../spill/useShowSpillBanner';
import { FAQs } from './dashboardWidgets/FAQs/FAQs';
import { OpenBankingStatusWidget } from './dashboardWidgets/openBankingStatusWidget/OpenBankingStatusWidget';
import { PromotionAccordion } from './dashboardWidgets/promotionAccordion/PromotionAccordion';
import { shouldShowPromotionAccordion } from './dashboardWidgets/promotionAccordion/shouldShowPromotionAccordion';
import { SidebarSectionTitle } from './dashboardWidgets/SidebarSectionTitle';
import { TabularFlexiDashboard } from './dashboardWidgets/tabularFlexiDashboard/TabularFlexiDashboard';
import NewLoanEndedView from './EndedView/NewLoanEndedView';
import { useGetMultiLoanDashboardData } from './useGetMultiLoanDashboardData';
import { useUkDynamicDashboardSplitTest } from './useUkDynamicDashboardSplitTest';
import { WidgetRenderer } from './WidgetRenderer/WidgetRenderer';
import { useTrackPageView } from '../../tracking/pageView';

const getAccountIdFromLocation = () => {
    const location = getCurrentLocation();
    const segments = location.pathname.split('/');
    return segments[2];
};

export const MultiLoanDashboard = () => {
    useTrackPageView({
        name: 'Flexi-12 Dashboard'
    });

    const accountId = getAccountIdFromLocation();

    const { features, isLoanEnded, loanStatus, topupStatus } =
        useGetMultiLoanDashboardData(accountId);

    const country = getCountry();
    const isDE = country === 'DE';
    const isUK = country === 'UK';
    const showPromotionAccordion = shouldShowPromotionAccordion(topupStatus);
    const { showSpillBanner, closeSpillBanner } = useShowSpillBanner();
    const { branch: ukDashboardBranch } = useUkDynamicDashboardSplitTest();
    const showDeprecatedDashboard =
        !isDE &&
        getCookie('dynamicAccountPages') !== 'true' &&
        ukDashboardBranch !== 'INCLUDE';

    const hasTrackedPromotion = useRef(false);
    useEffect(() => {
        if (showPromotionAccordion && !hasTrackedPromotion.current) {
            tracking.ampli.promotionalAccordionDisplayed();
            hasTrackedPromotion.current = true;
        }
    }, [showPromotionAccordion]);

    useEffect(() => {
        if (features && accountId) {
            tracking.ampli.dashboardViewed({
                account_id: accountId,
                product_type: features.revenue_based_loan
                    ? 'merchant_cash_advance'
                    : 'credit_facility',
                //@ts-expect-error
                dashboard_state: loanStatus,
                topup_status: topupStatus,
                dashboard_type: showDeprecatedDashboard ? 'tabular' : 'dynamic',
                uk_dynamic_dashboard_split_test: ukDashboardBranch
            });
        }
    }, [
        accountId,
        features,
        loanStatus,
        topupStatus,
        ukDashboardBranch,
        showDeprecatedDashboard
    ]);

    if (!accountId) {
        return null;
    }

    if (isLoanEnded) {
        return (
            <div className="m-auto mt-[-50px] grid w-full grid-cols-7 gap-x-4xl gap-y-2xl bg-structure-100 p-5xl max-sm:px-xl min-l:px-[10%] min-xl:px-[15%]">
                <NewLoanEndedView accountId={accountId} />
            </div>
        );
    }
    if (!features) {
        return null;
    }

    if (showDeprecatedDashboard) {
        return (
            <div
                data-testid="tabular-flexi-dashboard"
                className="m-auto mt-[-50px] block w-full grid-flow-dense grid-cols-7 gap-x-4xl gap-y-2xl space-y-l bg-transparent p-5xl min-sm:grid min-sm:space-y-0 max-sm:px-xl min-l:px-[10%] min-xl:px-[15%]"
            >
                <TabularFlexiDashboard accountId={accountId} />
            </div>
        );
    }

    return (
        <div
            className="bg-structure-100"
            data-testid="dynamic-multi-loan-dashboard"
        >
            <div className="mx-auto max-w-[1240px] px-l py-[50px]">
                {showSpillBanner && (
                    <SpillBanner onClose={closeSpillBanner} className="mb-xl" />
                )}
                {showPromotionAccordion && (
                    <div className="mb-xl min-l:hidden">
                        <PromotionAccordion />
                    </div>
                )}
                <div className="flex flex-col gap-xl min-l:flex-row">
                    <div className="flex-1 space-y-2xl">
                        <WidgetRenderer
                            accountId={accountId}
                            features={features}
                        />
                    </div>
                    <div className="flex-1 space-y-l min-l:max-w-[375px]">
                        {isUK && (
                            <div className="space-y-l">
                                <SidebarSectionTitle>
                                    <FormattedMessage
                                        id="_.sidebar.account_actions_title"
                                        defaultMessage="Account actions"
                                    />
                                </SidebarSectionTitle>
                                {showPromotionAccordion && (
                                    <div className="hidden min-l:block">
                                        <PromotionAccordion />
                                    </div>
                                )}
                                <OpenBankingStatusWidget />
                            </div>
                        )}
                        <FAQs accountId={accountId} />
                    </div>
                </div>
            </div>
        </div>
    );
};
